// components/PlayerPhotoUpload.js
// Simple photo upload component without avatar selection
// If no photo uploaded, PlayerAvatar component will show initials

import React, { useRef } from 'react';

const PlayerPhotoUpload = ({ currentPhotoUrl, onPhotoChange }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    console.log('📸 Processing photo file:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    try {
      // Resize and convert to base64
      const base64 = await resizeAndConvertToBase64(file);
      console.log('✅ Photo converted to base64 successfully');
      console.log('📤 Photo data being saved:', {
        hasPhotoUrl: !!base64,
        photoUrlLength: base64?.length || 0,
        photoUrlPreview: base64?.substring(0, 50) + '...'
      });
      
      // Call parent's onChange with base64 string (no defaultAvatar)
      onPhotoChange(base64);
    } catch (error) {
      console.error('❌ Error processing photo:', error);
      alert('Error processing photo. Please try another image.');
    }

    // Reset input
    event.target.value = '';
  };

  const resizeAndConvertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Create a square 200x200 canvas
          const canvas = document.createElement('canvas');
          const targetSize = 200;
          
          canvas.width = targetSize;
          canvas.height = targetSize;
          
          const ctx = canvas.getContext('2d');
          
          // Calculate dimensions to cover the square (center crop)
          let sourceWidth = img.width;
          let sourceHeight = img.height;
          let sourceX = 0;
          let sourceY = 0;
          
          // Determine crop dimensions to maintain aspect ratio and cover square
          const sourceAspect = sourceWidth / sourceHeight;
          
          if (sourceAspect > 1) {
            // Wider than tall - crop width
            sourceWidth = sourceHeight; // Make it square
            sourceX = (img.width - sourceHeight) / 2; // Center horizontally
          } else {
            // Taller than wide - crop height
            sourceHeight = sourceWidth; // Make it square
            sourceY = (img.height - sourceWidth) / 2; // Center vertically
          }
          
          // Draw cropped and scaled image
          ctx.drawImage(
            img,
            sourceX, sourceY, sourceWidth, sourceHeight, // Source rectangle (cropped)
            0, 0, targetSize, targetSize // Destination rectangle (200x200)
          );
          
          // Convert to base64 (JPEG with 0.8 quality for good compression)
          const base64 = canvas.toDataURL('image/jpeg', 0.8);
          
          console.log('✅ Photo resized:', {
            originalSize: file.size,
            originalDimensions: `${img.width}x${img.height}`,
            base64Length: base64.length,
            finalDimensions: `${targetSize}x${targetSize}`,
            reductionPercent: `${Math.round((1 - base64.length / file.size) * 100)}%`
          });
          
          resolve(base64);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = () => {
    if (window.confirm('Remove profile photo? Your initials will be displayed instead.')) {
      onPhotoChange('');
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        style={{
          padding: '8px 16px',
          background: '#2d6a4f',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 600,
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = '#1b4332';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = '#2d6a4f';
        }}
      >
        📸 {currentPhotoUrl ? 'Change Photo' : 'Upload Photo'}
      </button>
      
      {currentPhotoUrl && (
        <button
          type="button"
          onClick={handleRemovePhoto}
          style={{
            padding: '8px 16px',
            background: '#fff',
            color: '#dc2626',
            border: '1px solid #dc2626',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#fef2f2';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fff';
          }}
        >
          ✕ Remove
        </button>
      )}
    </div>
  );
};

export default PlayerPhotoUpload;
